import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Users, 
  Shield, 
  BarChart3,
  Check, 
  X,
  Lock,
  TrendingUp,
  Eye,
  Target,
  FileText,
  HelpCircle,
  Building2,
  CheckCircle2,
  Flag,
  Info
} from 'lucide-react';

// Brand colors
const BRAND = {
  navy: '#001F3F',
  red: '#FF4136',
  green: '#10B981',
  flagRed: '#F44336',
};

// Premium card styles - consistent with Home/Shortlist
const CARD_STYLES = {
  borderRadius: '14px',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
  border: '1px solid #E5E7EB',
};

// Hospital benchmark data - Updated to match copy ($595-$605 typical, $700 flagged ~16% over)
const HOSPITAL_DATA = [
  { id: 'A', name: 'Hospital A', price: 595, status: 'typical' },
  { id: 'B', name: 'Hospital B', price: 602, status: 'typical' },
  { id: 'C', name: 'Hospital C', price: 605, status: 'typical' },
  { id: 'D', name: 'Hospital D', price: 700, status: 'flagged', percentOver: 16 },
];

// Premium Hospital Card Component
const HospitalCard = ({ hospital, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  
  const isFlagged = hospital.status === 'flagged';
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const handleInteraction = () => {
    if (isFlagged) {
      setShowTooltip(!showTooltip);
    }
  };
  
  return (
    <div
      className="relative"
      style={{
        flex: '1 1 0',
        minWidth: '120px',
        maxWidth: '160px',
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.4s ease-out, transform 0.4s ease-out',
      }}
    >
      <div
        onClick={handleInteraction}
        onMouseEnter={() => { setIsHovered(true); if (isFlagged) setShowTooltip(true); }}
        onMouseLeave={() => { setIsHovered(false); setShowTooltip(false); }}
        className="relative cursor-pointer"
        style={{
          backgroundColor: isFlagged ? '#FEF8F8' : '#FFFFFF',
          borderRadius: '12px',
          border: isFlagged ? '1px solid #FECACA' : '1px solid #E5E7EB',
          padding: '16px 14px',
          boxShadow: isHovered 
            ? '0 4px 12px rgba(0,0,0,0.08)' 
            : '0 1px 3px rgba(0,0,0,0.04)',
          transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
          transition: 'all 0.2s ease',
        }}
      >
        {/* Hospital label */}
        <p 
          className="text-xs font-semibold tracking-wide uppercase mb-2"
          style={{ color: isFlagged ? '#B91C1C' : '#64748B' }}
        >
          {hospital.name}
        </p>
        
        {/* Price */}
        <p 
          className="text-xl font-bold tracking-tight"
          style={{ color: isFlagged ? '#991B1B' : BRAND.navy }}
        >
          ${hospital.price.toLocaleString()}
        </p>
        
        {/* Status indicator - bottom right */}
        <div 
          className="absolute bottom-2.5 right-2.5"
          style={{
            opacity: isHovered ? 1 : 0.7,
            transition: 'opacity 0.2s ease',
          }}
        >
          {isFlagged ? (
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(244,67,54,0.1)' }}
            >
              <Flag className="w-3.5 h-3.5" style={{ color: '#DC2626' }} strokeWidth={2} />
            </div>
          ) : (
            <div 
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}
            >
              <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#059669' }} strokeWidth={2} />
            </div>
          )}
        </div>
      </div>
      
      {/* Tooltip for flagged card */}
      {isFlagged && showTooltip && (
        <div 
          className="absolute -top-10 left-1/2 transform -translate-x-1/2 z-10"
          style={{
            opacity: showTooltip ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}
        >
          <div 
            className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap"
            style={{ 
              backgroundColor: '#1F2937', 
              color: '#FFFFFF',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            Flagged: ~{hospital.percentOver}% over typical range
          </div>
          <div 
            className="w-2 h-2 absolute left-1/2 -translate-x-1/2 -bottom-1 rotate-45"
            style={{ backgroundColor: '#1F2937' }}
          />
        </div>
      )}
    </div>
  );
};

// Fade-in animation component
const FadeInCard = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  
  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
      }}
    >
      {children}
    </div>
  );
};

const CommunityBenchmarks = () => {
  const navigate = useNavigate();
  const [showBenchmarkTooltip, setShowBenchmarkTooltip] = useState(false);

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#FAFBFC' }}>
      {/* Benchmark Explainer Modal */}
      {showBenchmarkTooltip && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={() => setShowBenchmarkTooltip(false)}
        >
          <div 
            className="mx-4 w-full max-w-sm"
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px' }}>
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="flex items-center justify-center"
                  style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '10px',
                    backgroundColor: `${BRAND.navy}08`,
                  }}
                >
                  <Info className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                </div>
                <h3 className="text-base font-bold" style={{ color: BRAND.navy }}>
                  What is a benchmark?
                </h3>
              </div>
              
              {/* Body text */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '16px' }}>
                <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.65' }}>
                  A benchmark is a reference range created from anonymous, aggregated data. It does not judge individual deals. It provides context.
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.65' }}>
                  Instead of asking, &quot;Is this good or bad?&quot; you can ask, &quot;Is this within the typical range for similar buyers and vehicles?&quot;
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.65' }}>
                  Benchmarks only appear when there is enough comparable data to be fair and meaningful.
                </p>
              </div>
              
              {/* Bottom note */}
              <div 
                style={{ 
                  backgroundColor: '#F8FAFC', 
                  borderRadius: '10px',
                  border: '1px solid #E5E7EB',
                  padding: '12px 14px',
                }}
              >
                <p className="text-xs leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                  No names. No individual contracts. Just ranges.
                </p>
              </div>
            </div>
            <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E7EB' }}>
              <button
                onClick={() => setShowBenchmarkTooltip(false)}
                className="w-full py-2.5 text-sm font-semibold transition-colors hover:opacity-90"
                style={{ 
                  backgroundColor: BRAND.navy, 
                  color: '#FFFFFF',
                  borderRadius: '10px',
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ 
        background: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div className="max-w-2xl mx-auto px-5 py-5">
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate('/')}
              className="p-1.5 -ml-1.5 rounded-lg transition-all duration-150 active:scale-95 hover:bg-gray-50"
              style={{ color: BRAND.navy }}
              data-testid="back-button"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
            </button>
            <span className="text-sm font-semibold" style={{ color: BRAND.navy }}>
              VINdicated.<span style={{ color: BRAND.red }}>ai</span>
            </span>
          </div>
          
          {/* Title with lock icon */}
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-xl font-bold tracking-tight" style={{ color: BRAND.navy }}>
              Community Price Benchmarks
            </h1>
            <Lock className="w-4 h-4" style={{ color: '#9CA3AF' }} strokeWidth={2} />
          </div>
          
          {/* Pill badge */}
          <span 
            className="inline-block px-3 py-1 text-xs font-medium rounded-full"
            style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}
          >
            Unlocks as our community grows
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-5 py-6">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          {/* CARD 1: HERO / WHY THIS EXISTS */}
          <FadeInCard delay={50}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-start gap-4 mb-5">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.red}10`,
                    }}
                  >
                    <Users className="w-5 h-5" style={{ color: BRAND.red }} strokeWidth={2} />
                  </div>
                  <div className="flex-1 pt-1">
                    <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                      Help the next buyer
                    </h2>
                  </div>
                </div>
                
                {/* Body */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    Car deals are difficult because buyers rarely know what others paid for the same vehicle.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    Two people can purchase the same model, trim, and options just miles apart — yet end up thousands of dollars apart in total cost.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    When enough buyers choose to opt in, VINdicated can publish anonymous, aggregated price ranges so you can sanity-check your deal before you sign.
                  </p>
                  
                  {/* Single sentence callout with tappable Benchmarks */}
                  <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>
                    <button
                      onClick={() => setShowBenchmarkTooltip(true)}
                      className="inline-flex items-center gap-0.5 transition-colors hover:opacity-80"
                      style={{ 
                        color: BRAND.navy,
                        fontWeight: 500,
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textUnderlineOffset: '2px',
                        textDecorationColor: '#9CA3AF',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                    >
                      Benchmarks
                    </button>
                    {' '}only unlock when there is enough similar data to be statistically fair. No individual deal is ever displayed. Only ranges.
                  </p>
                  
                  {/* Highlighted statement */}
                  <div 
                    style={{ 
                      backgroundColor: '#F8FAFC', 
                      borderRadius: '10px',
                      border: '1px solid #E5E7EB',
                      padding: '14px 16px',
                      marginTop: '4px',
                    }}
                  >
                    <p className="text-sm leading-relaxed font-medium" style={{ color: BRAND.navy }}>
                      This allows future buyers to answer one simple question: &quot;Does this deal look normal — or does it deserve a closer look?&quot;
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* CARD 2: A SIMPLE EXAMPLE FROM HEALTHCARE - Premium Hospital Cards */}
          <FadeInCard delay={100}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.navy}08`,
                    }}
                  >
                    <Building2 className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                    A simple example from healthcare
                  </h2>
                </div>
                
                {/* Intro text */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    In healthcare, hospitals often participate in a Group Purchasing Organization (GPO). A GPO allows hospitals to compare pricing across peers so no one hospital is quietly paying far more than others for the same implant or device.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    Benchmarks don&apos;t accuse anyone of wrongdoing. They provide a neutral reference point.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    If three hospitals are paying around $595–$605 for the same item and one is paying $700, the benchmark simply raises a fair question:
                  </p>
                </div>
                
                {/* Premium Hospital Cards Grid */}
                <div 
                  style={{ 
                    backgroundColor: '#F9FAFB',
                    borderRadius: '12px',
                    border: '1px solid #E5E7EB',
                    padding: '20px',
                    marginBottom: '20px',
                  }}
                >
                  {/* Cards container - responsive grid */}
                  <div className="flex flex-wrap gap-3 justify-center">
                    {HOSPITAL_DATA.map((hospital, index) => (
                      <HospitalCard 
                        key={hospital.id} 
                        hospital={hospital} 
                        delay={200 + (index * 80)}
                      />
                    ))}
                  </div>
                  
                  {/* Legend */}
                  <div className="flex items-center justify-center gap-6 mt-5 pt-4" style={{ borderTop: '1px solid #E5E7EB' }}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(16,185,129,0.1)' }}
                      >
                        <CheckCircle2 className="w-3 h-3" style={{ color: '#059669' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-xs font-medium" style={{ color: '#64748B' }}>Within typical range</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(244,67,54,0.1)' }}
                      >
                        <Flag className="w-3 h-3" style={{ color: '#DC2626' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-xs font-medium" style={{ color: '#64748B' }}>Flagged for review</span>
                    </div>
                  </div>
                </div>
                
                {/* Conclusion text */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    <span style={{ fontWeight: 600, color: BRAND.navy }}>&quot;Why are we outside the typical range?&quot;</span>
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    Sometimes there&apos;s a legitimate reason. Sometimes it&apos;s just a bad deal.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    Either way, benchmarks help decision-makers see what&apos;s normal before they commit.
                  </p>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* CARD 3: SAME IDEA FOR CAR DEALS */}
          <FadeInCard delay={150}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-center gap-4 mb-5">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.navy}08`,
                    }}
                  >
                    <BarChart3 className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                    Same idea for car deals
                  </h2>
                </div>
                
                {/* Body */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    We apply the same idea to car deals using out the door totals.
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.75' }}>
                    If one dealer&apos;s typical totals are consistently higher than what similar buyers pay elsewhere, you should be able to see that pattern before you sign.
                  </p>
                </div>
                
                {/* 3 step stepper - premium style */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                  {/* Step 1 */}
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div 
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%',
                          backgroundColor: BRAND.navy,
                        }}
                      >
                        <span className="text-xs font-bold text-white">1</span>
                      </div>
                      <div style={{ width: '2px', height: '20px', backgroundColor: '#E5E7EB', marginTop: '8px' }} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1.5 flex-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      Buyers enter their deal details, like saving a receipt.
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div 
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%',
                          backgroundColor: BRAND.navy,
                        }}
                      >
                        <span className="text-xs font-bold text-white">2</span>
                      </div>
                      <div style={{ width: '2px', height: '20px', backgroundColor: '#E5E7EB', marginTop: '8px' }} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1.5 flex-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      After entering your deal, you can choose to opt in to help improve community averages.
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div 
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%',
                          backgroundColor: BRAND.navy,
                        }}
                      >
                        <span className="text-xs font-bold text-white">3</span>
                      </div>
                      <div style={{ width: '2px', height: '20px', backgroundColor: '#E5E7EB', marginTop: '8px' }} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1.5 flex-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      We publish only ranges and sample size, never one person&apos;s exact deal.
                    </p>
                  </div>

                  {/* Note row */}
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div 
                        className="flex items-center justify-center flex-shrink-0"
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%',
                          backgroundColor: '#F3F4F6',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <span className="text-xs font-medium" style={{ color: BRAND.navy }}>→</span>
                      </div>
                    </div>
                    <p className="text-sm leading-relaxed pt-1.5 flex-1" style={{ color: '#6B7280', lineHeight: '1.7' }}>
                      Over time, transparency rewards dealers who price cleanly. When totals drift high, buyers spot it earlier and ask better questions before signing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* CARD 4: WHAT YOU WILL SEE LATER */}
          <FadeInCard delay={200}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.navy}08`,
                    }}
                  >
                    <Eye className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                    What you will see, later
                  </h2>
                </div>
                
                {/* 4 rows with feature row style */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <TrendingUp className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm font-semibold mb-1" style={{ color: BRAND.navy }}>Typical out the door range</p>
                      <p className="text-sm leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                        A range for similar buyers, plus how many deals are in the sample.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <BarChart3 className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm font-semibold mb-1" style={{ color: BRAND.navy }}>Common fee and add on patterns</p>
                      <p className="text-sm leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                        Helpful context on what tends to push totals up, while the out the door range stays the main benchmark.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <Target className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm font-semibold mb-1" style={{ color: BRAND.navy }}>Confidence signals</p>
                      <p className="text-sm leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                        Coverage and freshness so you can tell if the data is thin or strong.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '40px', 
                        height: '40px', 
                        borderRadius: '10px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <FileText className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm font-semibold mb-1" style={{ color: BRAND.navy }}>Plain English explanation</p>
                      <p className="text-sm leading-relaxed" style={{ color: '#6B7280', lineHeight: '1.6' }}>
                        A simple summary of what might be worth a second look before you sign.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* CARD 5: PRIVACY AND FAIRNESS */}
          <FadeInCard delay={250}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.navy}08`,
                    }}
                  >
                    <Shield className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                    Privacy and fairness, by design
                  </h2>
                </div>
                
                {/* Two column grid - equal height cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  {/* Keep column */}
                  <div 
                    className="flex flex-col h-full"
                    style={{ 
                      backgroundColor: '#F0FDF4', 
                      borderRadius: '12px',
                      border: '1px solid #BBF7D0',
                      padding: '18px',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div 
                        className="flex items-center justify-center"
                        style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '6px',
                          backgroundColor: '#DCFCE7',
                        }}
                      >
                        <Check className="w-3.5 h-3.5" style={{ color: '#16A34A' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-sm font-semibold" style={{ color: '#166534' }}>Keep</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Vehicle basics like year, make, model, trim, mileage
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Deal totals and common fees (doc fees, add-ons)
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Coarse location like state or metro area
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Time window like month and year
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Sample counts and ranges
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Dealership identity (name/location) used only for community averages
                      </p>
                    </div>
                  </div>
                  
                  {/* Never keep column */}
                  <div 
                    className="flex flex-col h-full"
                    style={{ 
                      backgroundColor: '#FEF2F2', 
                      borderRadius: '12px',
                      border: '1px solid #FECACA',
                      padding: '18px',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <div 
                        className="flex items-center justify-center"
                        style={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '6px',
                          backgroundColor: '#FEE2E2',
                        }}
                      >
                        <X className="w-3.5 h-3.5" style={{ color: '#DC2626' }} strokeWidth={2.5} />
                      </div>
                      <span className="text-sm font-semibold" style={{ color: '#991B1B' }}>Never keep</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Your name, email, phone, or address
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Anything that identifies you personally
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Uploaded documents or images used for community benchmarks
                      </p>
                      <p className="text-sm leading-relaxed" style={{ color: '#374151', lineHeight: '1.6' }}>
                        Your exact deal shown as a public example
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Trust paragraph - simplified */}
                <p className="text-xs leading-relaxed" style={{ color: '#9CA3AF', lineHeight: '1.6' }}>
                  Benchmarks appear only when there&apos;s enough similar opt-in data to be fair. We show ranges and sample size - not anyone&apos;s exact deal.
                </p>
              </div>
            </div>
          </FadeInCard>

          {/* CARD 6: FOR SKEPTICS */}
          <FadeInCard delay={300}>
            <div
              style={{
                backgroundColor: '#FFFFFF',
                borderRadius: CARD_STYLES.borderRadius,
                boxShadow: CARD_STYLES.shadow,
                border: CARD_STYLES.border,
              }}
            >
              <div style={{ padding: '24px' }}>
                {/* Icon and title */}
                <div className="flex items-center gap-4 mb-6">
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ 
                      width: '44px', 
                      height: '44px', 
                      borderRadius: '12px',
                      backgroundColor: `${BRAND.navy}08`,
                    }}
                  >
                    <HelpCircle className="w-5 h-5" style={{ color: BRAND.navy }} strokeWidth={2} />
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: BRAND.navy }}>
                    For skeptics
                  </h2>
                </div>
                
                {/* Checklist style rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '8px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <Shield className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      Benchmarks appear only when there is enough opt in data.
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '8px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <BarChart3 className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      We show sample size and ranges, not one off stories.
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div
                      className="flex items-center justify-center flex-shrink-0"
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        borderRadius: '8px',
                        backgroundColor: '#F8FAFC',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      <Check className="w-4 h-4" style={{ color: BRAND.navy }} strokeWidth={2} />
                    </div>
                    <p className="text-sm leading-relaxed pt-1" style={{ color: '#374151', lineHeight: '1.7' }}>
                      This is meant to inform your decision, not tell you what to do.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </FadeInCard>

          {/* Footer disclaimer */}
          <FadeInCard delay={350}>
            <div className="pt-2 pb-4">
              <p className="text-xs text-center leading-relaxed" style={{ color: '#9CA3AF' }}>
                Educational use only. Not financial, legal, or tax advice.
              </p>
            </div>
          </FadeInCard>
        </div>
      </div>
    </div>
  );
};

export default CommunityBenchmarks;
